import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getTrackingData, formatIQD } from '../utils/api';
import { Layers, FileText, CheckCircle, Clock, Package, DollarSign, Activity, AlertCircle } from 'lucide-react';

export default function ClientTrackingPage() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        getTrackingData(token)
            .then(setData)
            .catch(err => setError(err.response?.data?.error || 'حدث خطأ أثناء تحميل البيانات'))
            .finally(() => setLoading(false));
    }, [token]);

    if (loading) return <div className="min-h-screen flex items-center justify-center text-primary font-bold">جاري تحميل التقرير...</div>;
    if (error) return <div className="min-h-screen flex items-center justify-center text-danger font-bold flex-col gap-4"><AlertCircle size={48} /><span className="text-xl">{error}</span></div>;
    if (!data) return null;

    const { client, projects, subscriptions, invoices, deliverables, stats, invoice_terms } = data;
    const outstanding = stats.outstanding_iqd || 0;

    let termsHtml = '';
    if (invoice_terms && invoice_terms.trim()) {
        const termsLines = invoice_terms.split('\n').map(line => `<div class="py-0.5">${line}</div>`).join('');
        termsHtml = `
            <div class="font-bold mb-2 text-sm text-dark dark:text-gray-100">الشروط والأحكام</div>
            <div class="text-xs text-gray-600 dark:text-gray-300 leading-[1.8]">${termsLines}</div>
        `;
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-dark-deeper text-dark dark:text-gray-100 p-4 md:p-8 font-[Tajawal]" dir="rtl">
            <div className="max-w-5xl mx-auto space-y-6">

                {/* Header */}
                <div className="bg-white dark:bg-dark-deeper rounded-3xl p-6 md:p-8 shadow-sm border border-gray-200 dark:border-white/10">
                    <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
                        <div>
                            <p className="text-sm font-bold text-gray-500 dark:text-gray-400 mb-1">مرحباً بك،</p>
                            <h1 className="text-2xl md:text-3xl font-black text-dark dark:text-gray-100">{client.name}</h1>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 flex items-center gap-2">
                                <Activity size={16} /> تقرير حالة العمل والوضع المالي
                            </p>
                        </div>
                        <div className="text-right flex flex-col items-start md:items-end">
                            <p className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">الرصيد المتبقي (الديون)</p>
                            <p className={`text-2xl md:text-3xl font-black ${outstanding > 0 ? 'text-danger' : 'text-success'}`}>
                                {formatIQD(outstanding)}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Financials */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white dark:bg-dark-deeper rounded-2xl p-6 border border-gray-200 dark:border-white/10 shadow-sm flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                            <DollarSign size={24} />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">إجمالي الفواتير (كل الأوقات)</p>
                            <p className="text-xl font-black text-dark dark:text-gray-100">{formatIQD(stats.lifetime_value_iqd)}</p>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-dark-deeper rounded-2xl p-6 border border-gray-200 dark:border-white/10 shadow-sm flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-success/10 text-success flex items-center justify-center shrink-0">
                            <CheckCircle size={24} />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">إجمالي المدفوعات</p>
                            <p className="text-xl font-black text-dark dark:text-gray-100">{formatIQD(stats.total_paid_iqd)}</p>
                        </div>
                    </div>
                </div>

                {/* Content Tabs / Sections */}
                <div className="space-y-8">

                    {/* Active Subscriptions & Projects */}
                    {(subscriptions.length > 0 || projects.length > 0) && (
                        <section className="space-y-4">
                            <h2 className="text-lg font-black text-dark dark:text-gray-100 flex items-center gap-2">
                                <Package size={20} className="text-primary" /> الاشتراكات والمشاريع الحالية
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {subscriptions.map(s => {
                                    const usedPct = s.total_seconds > 0 ? Math.round((s.used_seconds * 100) / s.total_seconds) : 0;
                                    const isLow = usedPct >= 80;
                                    const progressColor = isLow ? 'bg-danger' : usedPct > 50 ? 'bg-warning' : 'bg-primary';
                                    const textColor = isLow ? 'text-danger' : usedPct > 50 ? 'text-warning' : 'text-primary';

                                    return (
                                        <div key={s.id} className={`bg-white dark:bg-dark-deeper border text-sm rounded-xl p-4 shadow-sm transition-all ${isLow && s.status === 'active' ? 'border-danger/30 ring-1 ring-danger/10' : 'border-gray-200 dark:border-white/10'}`}>
                                            <div className="flex justify-between items-start mb-2">
                                                <p className="font-bold text-dark dark:text-gray-100">{s.package_name}</p>
                                                <span className={`px-2.5 py-1 rounded-md text-xs font-bold ${s.status === 'active' ? 'bg-success/10 text-success' : 'bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400'}`}>
                                                    {s.status === 'active' ? 'نشط' : s.status}
                                                </span>
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-3 font-medium">الفترة: {s.period_start} إلى {s.period_end}</div>

                                            {/* Progress Bar */}
                                            <div className="bg-gray-50 dark:bg-white/5 p-3 rounded-lg border border-gray-100 dark:border-white/5 mt-1">
                                                <div className="flex justify-between items-end mb-2">
                                                    <span className="text-xs font-bold text-gray-500 dark:text-gray-400">استهلاك الرصيد</span>
                                                    <span className={`text-xs font-black ${textColor}`}>{usedPct}% مستهلك</span>
                                                </div>
                                                <div className="h-1.5 w-full bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden mb-2">
                                                    <div className={`h-full rounded-full transition-all duration-1000 ease-out ${progressColor}`} style={{ width: `${Math.min(usedPct, 100)}%` }} />
                                                </div>
                                                <div className="flex justify-between text-[11px] font-semibold">
                                                    <span className="text-gray-500 dark:text-gray-400">مستهلك: {s.used_seconds}ث</span>
                                                    <span className={textColor}>متبقي: {s.remaining_seconds}ث</span>
                                                    <span className="text-gray-500 dark:text-gray-400">الكلي: {s.total_seconds}ث</span>
                                                </div>
                                            </div>

                                            {isLow && s.status === 'active' && (
                                                <div className="mt-3 bg-danger/10 text-danger px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-2 border border-danger/20">
                                                    <AlertCircle size={14} /> رصيد منخفض، يرجى التجديد.
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {projects.filter(p => !['completed', 'cancelled', 'closed'].includes(p.status)).map(p => {
                                    const STAGE_LABELS = { ideas: 'كتابة الأفكار', approving: 'موافقة الأفكار', executing: 'تنفيذ الأفكار', done: 'تم التنفيذ' };
                                    return (
                                        <div key={p.id} className="bg-white dark:bg-dark-deeper border text-sm border-gray-200 dark:border-white/10 rounded-xl p-4 shadow-sm">
                                            <div className="flex justify-between items-start mb-2">
                                                <p className="font-bold text-dark dark:text-gray-100">{p.title}</p>
                                                <span className="px-2.5 py-1 rounded-md text-xs font-bold bg-blue-500/10 text-blue-600">مشروع</span>
                                            </div>
                                            <div className="flex flex-col gap-2 mt-2 pt-2 border-t border-gray-50 dark:border-white/5">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-gray-500 dark:text-gray-400 text-xs">الحالة العامة:</span>
                                                    <span className="font-bold text-dark dark:text-gray-100 text-xs">{p.status}</span>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-gray-500 dark:text-gray-400 text-xs">المرحلة الحالية:</span>
                                                    <span className="font-bold text-primary text-xs bg-primary/5 px-2 py-0.5 rounded">{STAGE_LABELS[p.current_stage] || 'قيد الـتخطيط'}</span>
                                                </div>
                                            </div>
                                            <div className="text-[10px] text-gray-400 mt-3 flex items-center gap-1">
                                                <Clock size={10} /> التسليم المتوقع: {p.due_date || 'غير محدد'}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {/* Deliverables */}
                    {deliverables.length > 0 && (
                        <section className="space-y-4">
                            <h2 className="text-lg font-black text-dark dark:text-gray-100 flex items-center gap-2">
                                <Layers size={20} className="text-primary" /> قائمة التسليمات
                            </h2>
                            <div className="bg-white dark:bg-dark-deeper border border-gray-200 dark:border-white/10 rounded-xl overflow-hidden shadow-sm">
                                <div className="flex flex-col">
                                    {deliverables.map((d, i) => (
                                        <div key={d.id} className={`p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 ${i !== deliverables.length - 1 ? 'border-b border-gray-100 dark:border-white/5' : ''}`}>
                                            <div>
                                                <p className="font-bold text-sm text-dark dark:text-gray-100">{d.title}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{d.package_name ? `ضمن: ${d.package_name}` : 'مشروع خاص'}</p>
                                            </div>
                                            <div className="flex items-center justify-start md:justify-end gap-4 text-xs md:text-sm">
                                                <span className="px-2 py-1 bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 rounded-md font-bold">{d.video_type || 'ملف'}</span>
                                                <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1"><Clock size={14} />{d.delivery_date}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>
                    )}

                    {/* Invoices */}
                    {invoices.length > 0 && (
                        <section className="space-y-4">
                            <h2 className="text-lg font-black text-dark dark:text-gray-100 flex items-center gap-2">
                                <FileText size={20} className="text-primary" /> سجل الفواتير
                            </h2>
                            <div className="bg-white dark:bg-dark-deeper border border-gray-200 dark:border-white/10 rounded-xl shadow-sm overflow-x-auto">
                                <table className="w-full text-right text-sm whitespace-nowrap">
                                    <thead className="bg-gray-50 dark:bg-white/5 border-b border-gray-200 dark:border-white/10">
                                        <tr>
                                            <th className="px-4 py-3 font-bold text-gray-500 dark:text-gray-400 shrink-0">رقم الفاتورة</th>
                                            <th className="px-4 py-3 font-bold text-gray-500 dark:text-gray-400">تاريخ الإصدار</th>
                                            <th className="px-4 py-3 font-bold text-gray-500 dark:text-gray-400">المبلغ الإجمالي</th>
                                            <th className="px-4 py-3 font-bold text-gray-500 dark:text-gray-400">المتبقي</th>
                                            <th className="px-4 py-3 font-bold text-gray-500 dark:text-gray-400">الحالة</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-white/5">
                                        {invoices.map(inv => {
                                            const remain = inv.total_iqd - inv.paid_iqd;
                                            const isPaid = inv.payment_status === 'paid';
                                            return (
                                                <tr key={inv.id} className="hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                                                    <td className="px-4 py-4 font-bold text-dark dark:text-gray-100">{inv.invoice_number || `#${inv.id}`}</td>
                                                    <td className="px-4 py-4 text-gray-500 dark:text-gray-400">{inv.issue_date}</td>
                                                    <td className="px-4 py-4 font-bold text-dark dark:text-gray-100">{formatIQD(inv.total_iqd)}</td>
                                                    <td className="px-4 py-4 font-bold text-danger">{remain > 0 ? formatIQD(remain) : formatIQD(0)}</td>
                                                    <td className="px-4 py-4">
                                                        <span className={`inline-block px-2.5 py-1.5 rounded-md text-xs font-bold border ${isPaid ? 'bg-success/10 text-success border-success/20' : remain > 0 && inv.payment_status === 'partial' ? 'bg-warning/10 text-warning border-warning/20' : 'bg-danger/10 text-danger border-danger/20'}`}>
                                                            {isPaid ? 'مدفوعة' : inv.payment_status === 'partial' ? 'مدفوعة جزئياً' : 'غير مدفوعة'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}

                    {/* Terms and Conditions */}
                    {termsHtml && (
                        <div className="bg-blue-50/50 dark:bg-white/5 border border-blue-100 dark:border-white/10 rounded-xl p-6 shadow-sm mt-8" dangerouslySetInnerHTML={{ __html: termsHtml }}>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="text-center pt-8 pb-4 text-xs font-bold text-gray-400">
                    تم إنشاء هذا التقرير آلياً. لا تتردد في التواصل معنا لأي استفسار.
                </div>
            </div>
        </div>
    );
}
